import { useEffect, useRef, useState } from "react";
import { ArrowLeft, FileText, Pencil, Sparkles } from "lucide-react";

import { TemplateFieldInput } from "@/components/contract-intelligence/templates/TemplateFieldInput";
import { TemplateIcon } from "@/components/contract-intelligence/templates/TemplateIcon";
import { Button, Progress, ScrollArea } from "@/components/ui";
import { getTemplateFlow } from "@/mockData/contractTemplates";
import type { ContractTemplate, ContractTemplateAnswers } from "@/types/contractIntelligence.types";

interface ContractTemplateChatFormProps {
  template: ContractTemplate;
  onBack: () => void;
  onComplete: (answers: ContractTemplateAnswers) => void;
}

function formatAnswer(value: string | string[] | undefined): string {
  if (!value) return "";
  return Array.isArray(value) ? value.join(", ") : value;
}

function previewSectionText(
  templateId: string,
  section: string,
  index: number,
  answers: ContractTemplateAnswers,
): string {
  const client = formatAnswer(answers.client ?? answers.artist ?? answers.parties);
  const brand = formatAnswer(answers.brand ?? answers.label);
  const term = formatAnswer(answers.term ?? answers.albums);
  const fee = formatAnswer(answers.fee ?? answers.advance ?? answers.value);
  const royalty = formatAnswer(answers.royalty ?? answers.rate ?? answers.commission);

  if (index === 0 && client) {
    return `This Agreement is entered into by ${client}${brand ? ` and ${brand}` : ""}.`;
  }
  if (index === 1 && term) {
    return `The Term shall be ${term}.`;
  }
  if (
    (index === 2 || section.toLowerCase().includes("compensation") || section.toLowerCase().includes("royalt")) &&
    (fee || royalty)
  ) {
    const parts: string[] = [];
    if (fee) parts.push(`Guaranteed compensation: $${fee.replace(/\D/g, "") || fee}.`);
    if (royalty) parts.push(`Royalty / commission: ${royalty}${royalty.includes("%") ? "" : "%"}.`);
    return parts.join(" ");
  }
  if (Object.keys(answers).length > index) {
    return "[Section will be drafted from your answers.]";
  }
  return "";
}

export function ContractTemplateChatForm({ template, onBack, onComplete }: ContractTemplateChatFormProps) {
  const flow = getTemplateFlow(template.id);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<ContractTemplateAnswers>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  const progress = flow.length > 0 ? (step / flow.length) * 100 : 0;
  const current = flow[step];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [step]);

  const submitAnswer = (value: string | string[]) => {
    if (!current) return;
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    if (step + 1 >= flow.length) {
      onComplete(next);
    } else {
      setStep((s) => s + 1);
    }
  };

  return (
    <div className="flex w-full h-full min-h-0 overflow-hidden">
      <section className="flex w-full min-w-0 flex-1 flex-col border-r border-border">
        <header className="flex shrink-0 items-center gap-3 border-b border-border px-5 py-3.5">
          <Button type="button" variant="outline" size="icon" className="size-8" onClick={onBack} aria-label="Back">
            <ArrowLeft className="size-4" />
          </Button>
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-white"
            style={{ background: `linear-gradient(135deg, ${template.color}, ${template.color}cc)` }}
          >
            <TemplateIcon name={template.icon} className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold">{template.name}</p>
            <p className="text-[11px] text-muted-foreground">
              Step {step + 1} of {flow.length} · ~{Math.max(1, flow.length - step)} min remaining
            </p>
          </div>
          <span className="font-mono text-[10.5px] text-muted-foreground">{Math.round(progress)}%</span>
        </header>
        <Progress value={progress} className="h-0.5 shrink-0 rounded-none" />

        <ScrollArea className="min-h-0 flex-1">
          <div ref={scrollRef} className="space-y-5 px-5 py-6">
            {flow.slice(0, step + 1).map((field, index) => {
              const answered = index < step;
              const answer = answers[field.id];
              return (
                <article key={field.id}>
                  <div className="mb-2.5 flex gap-2.5">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-violet-500">
                      <Sparkles className="size-3 text-white" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10.5px] font-medium text-muted-foreground">Ari</p>
                      <p className="text-[13.5px] leading-relaxed">{field.question}</p>
                      {field.hint ? <p className="mt-1 text-[11.5px] italic text-muted-foreground">{field.hint}</p> : null}
                      {field.suggest && index === step ? (
                        <p className="mt-2 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-2 text-[11.5px] text-muted-foreground">
                          <span className="font-semibold text-primary">Benchmark · </span>
                          {field.suggest}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {answered && answer ? (
                    <p className="ml-9 max-w-[70%] rounded-xl rounded-br-sm border border-border bg-muted/40 px-3 py-2 text-xs">
                      {formatAnswer(answer)}
                    </p>
                  ) : null}
                  {index === step ? <TemplateFieldInput field={field} onSubmit={submitAnswer} /> : null}
                </article>
              );
            })}
          </div>
        </ScrollArea>
      </section>

      <aside className="flex w-[380px] shrink-0 flex-col overflow-hidden bg-muted/20">
        <header className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3.5">
          <FileText className="size-3.5 text-primary" aria-hidden="true" />
          <span className="text-[11px] font-semibold tracking-wide">Live draft preview</span>
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">{template.pages} pp</span>
        </header>
        <ScrollArea className="min-h-0 flex-1">
          <div className="p-5">
            <article className="min-h-[500px] rounded-md bg-white px-7 py-8 font-serif text-sm leading-relaxed text-[#1a1a2e] shadow-lg">
              <header className="mb-6 text-center">
                <p className="text-[9.5px] tracking-[0.2em] text-[#6c708a] uppercase">Equal IQ · Confidential Draft</p>
                <h2 className="mt-2 text-lg font-semibold tracking-tight">{template.name.toUpperCase()}</h2>
                <p className="mt-1 text-[10px] text-[#6c708a]">
                  Generated by Ari · {new Date().toLocaleDateString()}
                </p>
              </header>
              <hr className="mb-4 border-[#1a1a2e22]" />
              {template.sections.map((section, index) => {
                const text = previewSectionText(template.id, section, index, answers);
                return (
                  <section key={section} className="mb-3.5">
                    <h3 className="mb-1 text-[10.5px] font-bold tracking-wide">
                      § {index + 1}. {section.toUpperCase()}
                    </h3>
                    {text ? (
                      <p className="text-[11px] leading-relaxed text-[#3a3f5f]">{text}</p>
                    ) : (
                      <div className="space-y-1">
                        {[0, 1, 2].map((i) => (
                          <span
                            key={i}
                            className="block h-2 rounded-sm bg-[#1a1a2e08]"
                            style={{ width: `${70 + i * 8}%` }}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
              <p className="mt-6 border-t border-[#1a1a2e22] pt-3 text-center text-[10px] text-[#6c708a]">
                {template.pages - template.sections.length} additional pages of standard terms · auto-attached
              </p>
            </article>
          </div>
        </ScrollArea>
        <footer className="flex shrink-0 gap-2 border-t border-border p-3">
          <Button type="button" variant="outline" size="sm" className="flex-1 text-[11.5px]" disabled>
            <Pencil className="size-3" aria-hidden="true" />
            Edit manually
          </Button>
          <Button type="button" variant="outline" size="sm" className="flex-1 text-[11.5px]" disabled>
            <FileText className="size-3" aria-hidden="true" />
            Download .docx
          </Button>
        </footer>
      </aside>
    </div>
  );
}
